import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoadingOverlay from 'react-loading-overlay';
import ScoreListItem from './components/ScoreListItem';
import ProgressBar from './components/ProgressBar';
import './StressLanding.scss';
import assessmentImage from './images';
import { commonConstant } from '../../common';
import { fetchHealthSpanProfileAction } from '../../actions/healthSpanActions';
import ConfirmButton from './components/ConfirmButton';
import { useScoreCategoryMetadata } from './useScoreCategoryMetadata';
import {
  HEALTH_SPAN_MAX_SCORE,
  SUB_CATEGORY_KEY_MAP,
  buildLandingScores,
  hasValidScore,
  getSubCategoryDisplayProps,
} from './healthSpanScore.utils';

const ASSESSMENT_IDS = {
  stressLevel: process.env.REACT_APP_STRESS_ASSESSMENT_ID || '',
};

const CATEGORIES = [
  {
    key: 'stressLevel',
    image: assessmentImage.gutGroupImage,
    name: 'ระดับความเครียด',
  },
];

const StressLanding = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { profile, loading } = useSelector((state) => state.healthSpan);
  const { metadata, metaLoading } = useScoreCategoryMetadata('stress');

  useEffect(() => {
    const wrapper = document.getElementById('wrapper');
    if (wrapper) wrapper.style.maxWidth = '100%';
    return () => {
      if (wrapper) wrapper.style.maxWidth = '';
    };
  }, []);

  useEffect(() => {
    dispatch(fetchHealthSpanProfileAction());
  }, [dispatch]);

  const { categoryScore, categoryStatus, scores } = buildLandingScores(
    profile,
    'stress',
    CATEGORIES,
    metadata
  );

  const hasValue = hasValidScore(categoryScore);
  const isLoading = loading || metaLoading;
  const tip = categoryStatus?.tip;
  const noDataColor = '#A1A1A1';
  const displayColor = hasValue ? categoryStatus.barColor : noDataColor;

  const handleCategoryClick = (category) => {
    const assessmentId = ASSESSMENT_IDS[category.key];
    if (!assessmentId) return;

    history.push(
      `${commonConstant.pathAssessmentQuestion}?assessment_id=${assessmentId}&return=${commonConstant.pathStressAssessmentLanding}&section=${category.key}`
    );
  };

  const addUpdateButton = () => {
    const stressId = process.env.REACT_APP_STRESS_ASSESSMENT_ID || '';
    history.push(`${commonConstant.pathStressTest}?id=${stressId}&return=${encodeURIComponent(commonConstant.pathStressAssessmentLanding)}`);
  };

  const headerScore = hasValue ? categoryScore : '-';
  const headerMaxScore = categoryStatus?.maxScore || HEALTH_SPAN_MAX_SCORE;

  return (
    <div className="stress-landing-component">
      {isLoading && (
        <LoadingOverlay
          text="Loading"
          spinner
          active={true}
          styles={{ wrapper: { width: '100%', height: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 10 } }}
        >
          <div />
        </LoadingOverlay>
      )}

      {!isLoading && (
        <>
          <div className="bg-detail-container">
            <img src={assessmentImage.landingBgDetail} alt="" className="bg-detail" />
          </div>

          <div className="content-container">
            <div className="header-section">
              <div className="image-component">
                <img src={assessmentImage.stressImage} alt="" />
              </div>
              <div className="detail" style={{ color: displayColor }}>
                <div className="header-name">{metadata?.display_name || 'ความเครียด'}</div>
                <div className="status">{hasValue ? categoryStatus.statusText : 'ไม่มีข้อมูล'}</div>
                <div className="score-container">
                  <div className="value">{headerScore}</div>
                  <div className="max-value">/{headerMaxScore}</div>
                </div>
                <ProgressBar
                  value={hasValue ? categoryScore : 0}
                  maxValue={headerMaxScore}
                  width="130px"
                  barColor={displayColor}
                />
              </div>
            </div>

            {hasValue && tip && (
              <div className="tip-card">
                <div className="tip-label-container">
                  <img src={assessmentImage.tipIcon} alt="" className="tip-label-image" />
                  <div className="tip-label-text">คำแนะนำ</div>
                </div>
                <div className="tip-detail-container">
                  <img
                    src={tip.image || assessmentImage.tipStressImage || assessmentImage.tipGutImage}
                    alt=""
                    className="image"
                  />
                  <div className="text-container">
                    <div className="tip-text">{tip.title}</div>
                    <div className="tip-detail-text">{tip.description}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="summary-score-list">
              <div className="title">ข้อมูลความเครียด</div>

              {CATEGORIES.map((category) => {
                const scoreData = scores[category.key] || {};
                const hasData = hasValidScore(scoreData.score);
                const apiKey = SUB_CATEGORY_KEY_MAP[category.key];
                const subName =
                  metadata?.sub_categories?.[apiKey]?.display_name || category.name;
                const display = getSubCategoryDisplayProps(scoreData, hasData);

                // Use raw stress assessment score from details
                const stressDetail = profile?.scoreDetails?.stress?.details;
                const rawStressScore = stressDetail?.stress?.score;
                const displayScore = rawStressScore !== undefined && rawStressScore !== null
                  ? Math.round(rawStressScore)
                  : (hasData ? scoreData.score : '-');
                const hasDisplayData = rawStressScore !== undefined && rawStressScore !== null
                  ? hasValidScore(rawStressScore)
                  : hasData;
                const displayProps = getSubCategoryDisplayProps(scoreData, hasDisplayData);
                const subMaxScore = metadata?.sub_categories?.[apiKey]?.max_score
                  || scoreData.maxScore || HEALTH_SPAN_MAX_SCORE;

                return (
                  <ScoreListItem
                    key={category.key}
                    image={category.image}
                    name={subName}
                    statusText={displayProps.statusText}
                    statusColor={displayProps.barColor}
                    value={hasDisplayData ? displayScore : '-'}
                    maxValue={subMaxScore}
                    barColor={displayProps.barColor}
                    valueColor={displayProps.valueColor}
                    hasData={hasDisplayData}
                    clickable={false}
                  />
                );
              })}
            </div>

            <div className="description-card">
              <div className="title">ความเครียด (Stress & Well-being)</div>
              <div className="description-text">
                <span className="highlight">ประเมินระดับความเครียดและผลกระทบ</span>
                ที่มีต่อร่างกาย และจิตใจ เพื่อช่วยให้คุณเข้าใจสมดุลทางอารมณ์ และการฟื้นตัวจากความเครียด
              </div>
            </div>
          </div>
        </>
      )}

      <div className="footer-container">
        <ConfirmButton text={hasValue ? 'อัปเดทข้อมูล' : 'เพิ่มข้อมูล'} width={'100%'} color={'black'} onClick={addUpdateButton} />
      </div>
    </div>
  );
};

export default StressLanding;
